import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Stack,
  Autocomplete,
  CircularProgress,
} from '@mui/material';

import 'dayjs/locale/ko';

import BaseCodeSelectbox from '../../common/form/selectbox/BaseCodeSelectbox';
import PartnerModalConatiner from '../../../containers/common/form/modal/PartnerModalConatiner';
import FormLabel from '../../common/form/FormLabel';
import EmpModalContainer from '../../../containers/common/form/modal/EmpModalContainer';
import * as partnerApi from '../../../lib/api/partner';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as dayjs from 'dayjs';
import ShareLinkBox from '../../common/ShareLinkBox';
import GroupPicker from '../group/GroupPicker';

const ChanceDetailForm = ({ formik, chance, isFieldEnabled, isFieldRequired }) => {
  // isFieldEnabled가 없으면 기본 모두 표시 (fallback)
  const fieldEnabled = isFieldEnabled || (() => true);
  const fieldRequired = isFieldRequired || (() => false);
  const [expDay, setExpDay] = useState(null);
  const [ordrEstDate, setOrdrEstDate] = useState(null);

  // 담당자 관련 상태
  const [contactList, setContactList] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    if (chance) {
      if (chance.expDay) {
        setExpDay(dayjs(chance.expDay));
      }
      if (chance.ordrEstDate) {
        setOrdrEstDate(dayjs(chance.ordrEstDate));
      }
    }
  }, [chance]);

  // 고객사(customerUuid) 기반 담당자 목록 조회
  const fetchPartnerContacts = async (partnerUuid) => {
    if (!partnerUuid) {
      setContactList([]);
      return;
    }
    setLoadingContacts(true);
    try {
      const res = await partnerApi.getPartnerEmp(partnerUuid);
      setContactList(res.data?.list || []);
    } catch (err) {
      console.error('담당자 조회 실패', err);
      setContactList([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  // 페이지 로드 시 기존 customerUuid가 있으면 담당자 목록 조회
  useEffect(() => {
    if (formik.values.customerUuid) {
      fetchPartnerContacts(formik.values.customerUuid);
    }
  }, [formik.values.customerUuid]);

  // 담당자 선택 시 정보 자동 채움
  const handleContactSelect = (event, contact) => {
    setSelectedContact(contact);
    if (contact) {
      formik.setFieldValue('chargeNm', contact.employeeNm || '');
      formik.setFieldValue('chargeEml', contact.emailAddr || '');
      formik.setFieldValue('chargeTel', contact.telNo || '');
      formik.setFieldValue('vendorEmp', contact.employeeUuid || '');
    } else {
      formik.setFieldValue('chargeNm', '');
      formik.setFieldValue('chargeEml', '');
      formik.setFieldValue('chargeTel', '');
      formik.setFieldValue('vendorEmp', '');
    }
  };

  const onChangeDate = (newValue, name) => {
    formik.setFieldValue(name, newValue);
  };

  // Vendor 
  const [openVendor, setOpenVendor] = useState(false);
  const handleOpenVendor = () => {
    setOpenVendor(true);
  };
  const handleCloseVendor = () => {
    setOpenVendor(false);
  };

  const selectedVendor = (partner) => {
    formik.setFieldValue('vendorCmp', partner.partnerUuid);
    formik.setFieldValue('vendorCmpNm', partner.partnerNm);
  };

  // Customer 
  const [openCustomer, setOpenCustomer] = useState(false);
  const handleOpenCustomer = () => {
    setOpenCustomer(true);
  };
  const handleCloseCustomer = () => {
    setOpenCustomer(false);
  };

  const selectedCustomer = (partner) => {
    formik.setFieldValue('customerUuid', partner.partnerUuid);
    formik.setFieldValue('customerNm', partner.partnerNm);

    // 담당자 정보 초기화 및 목록 재조회
    formik.setFieldValue('chargeNm', '');
    formik.setFieldValue('chargeEml', '');
    formik.setFieldValue('chargeTel', '');
    formik.setFieldValue('vendorEmp', '');
    setSelectedContact(null);
    fetchPartnerContacts(partner.partnerUuid);
  };

  // Employee
  const [openEmp, setOpenEmp] = useState(false);
  const [title, setTitle] = useState('사원찾기');
  const openEmpModal = (type, title) => {
    setTitle(title);
    setOpenEmp(true);
  };
  const handleEmpClose = () => {
    setOpenEmp(false);
  };

  const selectedEmployee = (emp) => {
    formik.setFieldValue(`crtNm`, emp.usrNm, true);
    formik.setFieldValue(`crtUsr`, emp.usrUuid, false);
  };

  // 필수 필드 마크
  const requiredMark = (
    <Typography
      variant="subtitle2"
      component="span"
      sx={{ ml: 0.5, color: 'error.main' }}
    >
      *
    </Typography>
  );

  // 섹션 헤더 컴포넌트
  const SectionHeader = ({ title }) => (
    <Typography 
      variant="h6" 
      sx={{ 
        mb: 2, 
        fontWeight: 'medium',
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        paddingBottom: 0.5,
        display: 'inline-block'
      }}
    >
      {title}
    </Typography>
  );

  return (
    <div>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 2, flexWrap: 'wrap',
        }}>
          <SectionHeader title="프로젝트 기본 정보" />
          {/* 저장된 건에만 나온다. 아직 번호가 없는 새 건에는 공유할 주소도 없다. */}
          <ShareLinkBox path={formik.values?.prjNo ? `/go/${formik.values.prjNo}` : null} />
        </Box>

        {/*
          소속 묶음. 고르는 즉시 저장한다 - 분류를 바꾸는 것뿐이라 기회 폼의 저장 버튼과
          엮을 이유가 없고, 엮으면 "묶기만 했는데 왜 저장을 눌러야 하나" 가 된다.
        */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormLabel>소속 묶음</FormLabel>
            <GroupPicker
              prjUuid={chance?.prjUuid}
              value={chance?.groupUuid}
              onChanged={(v) => { if (chance) chance.groupUuid = v; }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {fieldEnabled('prjTyp') && (
            <Grid item xs={12} md={6}>
              <FormLabel>프로젝트 타입{fieldRequired('prjTyp') ? requiredMark : null}</FormLabel>
              <BaseCodeSelectbox
                formik={formik}
                filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'PRJ_TYP', useYn: 'Y' }}
                name="prjTyp" size="small" required={fieldRequired('prjTyp')} fullWidth
              />
            </Grid>
          )}
          {fieldEnabled('incTyp') && (
            <Grid item xs={12} md={6}>
              <FormLabel>거래처 유형{fieldRequired('incTyp') ? requiredMark : null}</FormLabel>
              <BaseCodeSelectbox
                formik={formik}
                filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'INC_TYP', useYn: 'Y' }}
                name="incTyp" size="small" required={fieldRequired('incTyp')} fullWidth
              />
            </Grid>
          )}
          {fieldEnabled('cstNm') && (
            <Grid item xs={12} md={6}>
              <FormLabel>사업명{fieldRequired('cstNm') ? requiredMark : null}</FormLabel>
              <TextField
                size="small" required={fieldRequired('cstNm')} variant="outlined" fullWidth
                id="cstNm" name="cstNm" value={formik.values.cstNm} onChange={formik.handleChange}
                error={formik.touched.cstNm && Boolean(formik.errors.cstNm)}
                helperText={formik.touched.cstNm && formik.errors.cstNm}
              />
            </Grid>
          )}
          {fieldEnabled('prjImpt') && (
            <Grid item xs={12} md={6}>
              <FormLabel>프로젝트 중요도{fieldRequired('prjImpt') ? requiredMark : null}</FormLabel>
              <BaseCodeSelectbox
                formik={formik}
                filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'PRJ_IMPT', useYn: 'Y' }}
                name="prjImpt" size="small" required={fieldRequired('prjImpt')} fullWidth
              />
            </Grid>
          )}
          {fieldEnabled('bugetYn') && (
            <Grid item xs={12} md={4}>
              <FormLabel>예산여부{fieldRequired('bugetYn') ? requiredMark : null}</FormLabel>
              <BaseCodeSelectbox
                formik={formik}
                filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'BUGET_YN', useYn: 'Y' }}
                name="bugetYn" size="small" required={fieldRequired('bugetYn')} fullWidth
              />
            </Grid>
          )}
          {fieldEnabled('productYn') && (
            <Grid item xs={12} md={4}>
              <FormLabel>제품여부{fieldRequired('productYn') ? requiredMark : null}</FormLabel>
              <BaseCodeSelectbox
                formik={formik}
                filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'PRD_YN', useYn: 'Y' }}
                name="productYn" size="small" required={fieldRequired('productYn')} fullWidth
              />
            </Grid>
          )}
          {fieldEnabled('procurement') && (
            <Grid item xs={12} md={4}>
              <FormLabel>조달여부</FormLabel>
              <Box sx={{ mt: 1 }}>
                <FormGroup>
                  <FormControlLabel
                    id="procurement" name="procurement" label="조달"
                    control={
                      <Checkbox
                        checked={formik.values.procurement}
                        onChange={(event) => formik.setFieldValue('procurement', event.target.checked)}
                      />
                    }
                  />
                </FormGroup>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormLabel>영업 담당자</FormLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small" variant="outlined" disabled fullWidth
                id="crtNm" name="crtNm" value={formik.values.crtNm} onChange={formik.handleChange}
                error={formik.touched.crtNm && Boolean(formik.errors.crtNm)}
                helperText={formik.touched.crtNm && formik.errors.crtNm}
              />
              <Button variant="outlined" size="small" onClick={() => openEmpModal('', '영업담당자 선택')} sx={{ whiteSpace: 'nowrap' }}>
                담당자 선택
              </Button>
            </Box>
          </Grid>
          {fieldEnabled('expDay') && (
            <Grid item xs={12} md={6}>
              <FormLabel>매출(수금)예정일{fieldRequired('expDay') ? requiredMark : null}</FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                <DatePicker
                  format="YYYY-MM-DD" id="expDay" name="expDay" value={expDay}
                  onChange={(newValue) => { setExpDay(newValue); onChangeDate(newValue, 'expDay'); }}
                  slotProps={{ textField: { size: 'small', required: fieldRequired('expDay'), fullWidth: true, error: formik.touched.expDay && Boolean(formik.errors.expDay), helperText: formik.touched.expDay && formik.errors.expDay } }}
                />
              </LocalizationProvider>
            </Grid>
          )}
          {fieldEnabled('ordrEstDate') && (
            <Grid item xs={12} md={6}>
              <FormLabel>계약 예정일{fieldRequired('ordrEstDate') ? requiredMark : null}</FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                <DatePicker
                  format="YYYY-MM-DD" id="ordrEstDate" name="ordrEstDate" value={ordrEstDate}
                  onChange={(newValue) => { setOrdrEstDate(newValue); onChangeDate(newValue, 'ordrEstDate'); }}
                  slotProps={{ textField: { size: 'small', required: fieldRequired('ordrEstDate'), fullWidth: true, error: formik.touched.ordrEstDate && Boolean(formik.errors.ordrEstDate), helperText: formik.touched.ordrEstDate && formik.errors.ordrEstDate } }}
                />
              </LocalizationProvider>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <SectionHeader title="거래처 정보" />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {fieldEnabled('partnerInc') && (
            <Grid item xs={12} md={6}>
              <FormLabel>고객사{fieldRequired('partnerInc') ? requiredMark : null}</FormLabel>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small" variant="outlined" disabled InputLabelProps={{ shrink: true }} fullWidth
                  id="customerNm" name="customerNm" value={formik.values.customerNm}
                  error={formik.touched.customerNm && Boolean(formik.errors.customerNm)}
                  helperText={formik.touched.customerNm && formik.errors.customerNm}
                />
                <Button color="secondary" variant="outlined" size="small" onClick={handleOpenCustomer} sx={{ whiteSpace: 'nowrap' }}>
                  고객사 선택
                </Button>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormLabel>매출처</FormLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small" variant="outlined" disabled InputLabelProps={{ shrink: true }} fullWidth
                id="vendorCmpNm" name="vendorCmpNm" value={formik.values.vendorCmpNm}
                error={formik.touched.vendorCmpNm && Boolean(formik.errors.vendorCmpNm)}
                helperText={formik.touched.vendorCmpNm && formik.errors.vendorCmpNm}
              />
              <Button color="secondary" variant="outlined" size="small" onClick={handleOpenVendor} sx={{ whiteSpace: 'nowrap' }}>
                매출처 선택
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {(fieldEnabled('chargeNm') || fieldEnabled('chargeEml') || fieldEnabled('chargeTel')) && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <SectionHeader title="담당자 정보" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <FormLabel>담당자 선택</FormLabel>
              <Autocomplete
                size="small"
                options={contactList}
                getOptionLabel={(option) =>
                  option ? `${option.employeeNm || ''}${option.dutiesNm ? ` (${option.dutiesNm})` : ''}` : ''
                }
                value={selectedContact}
                onChange={handleContactSelect}
                loading={loadingContacts}
                disabled={!formik.values.customerUuid}
                noOptionsText={formik.values.customerUuid ? '등록된 담당자가 없습니다' : '고객사를 먼저 선택하세요'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={formik.values.customerUuid ? '담당자를 선택하세요' : '고객사를 먼저 선택하세요'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option?.employeeUuid === value?.employeeUuid}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                담당자를 선택하면 아래 정보가 자동으로 입력됩니다. 직접 입력도 가능합니다.
              </Typography>
            </Grid>
            {fieldEnabled('chargeNm') && (
              <Grid item xs={12} md={6}>
                <FormLabel>고객 담당자{fieldRequired('chargeNm') ? requiredMark : null}</FormLabel>
                <TextField
                  size="small" required={fieldRequired('chargeNm')} variant="outlined" fullWidth
                  id="chargeNm" name="chargeNm" value={formik.values.chargeNm} onChange={formik.handleChange}
                  error={formik.touched.chargeNm && Boolean(formik.errors.chargeNm)}
                  helperText={formik.touched.chargeNm && formik.errors.chargeNm}
                />
              </Grid>
            )}
            {fieldEnabled('chargeTel') && (
              <Grid item xs={12} md={6}>
                <FormLabel>고객담당자 연락처{fieldRequired('chargeTel') ? requiredMark : null}</FormLabel>
                <TextField
                  size="small" variant="outlined" fullWidth
                  id="chargeTel" name="chargeTel" value={formik.values.chargeTel} onChange={formik.handleChange}
                  error={formik.touched.chargeTel && Boolean(formik.errors.chargeTel)}
                  helperText={formik.touched.chargeTel && formik.errors.chargeTel}
                />
              </Grid>
            )}
            {fieldEnabled('chargeEml') && (
              <Grid item xs={12}>
                <FormLabel>고객담당자 이메일{fieldRequired('chargeEml') ? requiredMark : null}</FormLabel>
                <TextField
                  size="small" variant="outlined" fullWidth
                  id="chargeEml" name="chargeEml" value={formik.values.chargeEml} onChange={formik.handleChange}
                  error={formik.touched.chargeEml && Boolean(formik.errors.chargeEml)}
                  helperText={formik.touched.chargeEml && formik.errors.chargeEml}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {fieldEnabled('forcastTxt') && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <SectionHeader title="추가 정보" />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormLabel>참고사항{fieldRequired('forcastTxt') ? requiredMark : null}</FormLabel>
              <TextField
                size="small" variant="outlined" fullWidth multiline rows={4}
                id="forcastTxt" name="forcastTxt" value={formik.values.forcastTxt} onChange={formik.handleChange}
                error={formik.touched.forcastTxt && Boolean(formik.errors.forcastTxt)}
                helperText={formik.touched.forcastTxt && formik.errors.forcastTxt}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
        <Button
          variant="outlined"
          sx={{ minWidth: '100px' }}
        >
          취소
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={formik.isSubmitting}
          sx={{ minWidth: '100px' }}
        >
          저장
        </Button>
      </Box>

      {openVendor && (
        <PartnerModalConatiner
          link
          title="매출처 선택"
          open={openVendor}
          handleClose={handleCloseVendor}
          selectedPartner={selectedVendor}
        />
      )}
      
      {openCustomer && (
        <PartnerModalConatiner
          link
          title="고객사 선택"
          open={openCustomer}
          handleClose={handleCloseCustomer}
          selectedPartner={selectedCustomer}
        />
      )}

      {openEmp && (
        <EmpModalContainer
          title={title}
          multi={false}
          open={openEmp}
          roleTyp="SALES"
          handleClose={handleEmpClose}
          selectedEmployee={selectedEmployee}
        />
      )}
    </div>
  );
};

export default ChanceDetailForm;